import React from 'react';
import QuestionHeader from './header';
import QuestionView from './question';
import useQuestionPage from '../../../hooks/useQuestionPage';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import Separator from '../../ui/separator';
import { Alert, AlertDescription } from '../../ui/alert';
import Input from '../../ui/input';
import AskQuestionButton from '../askQuestionButton';
import BreadcrumbNav, { BreadcrumbItemProps } from '../../common/BreadcrumbNav';

/**
 * QuestionPage component renders a page displaying a list of questions
 * based on filters such as order and search terms.
 * It includes a header with order buttons and a button to ask a new question.
 */
const QuestionPage = () => {
  const {
    val,
    handleInputChange,
    handleKeyDown,
    titleText,
    qlist,
    questionOrder,
    setQuestionOrder,
    loading,
    error,
  } = useQuestionPage();

  const renderLoadingState = () => (
    <div className='flex justify-center p-8'>
      <div className='h-8 w-8 animate-spin rounded-full border-b-2 border-primary'></div>
    </div>
  );

  const renderNoQuestionsState = () => (
    <div className='text-center py-12'>
      <h3 className='text-lg font-medium mb-2'>No Questions Found</h3>
      <p className='text-muted-foreground mb-4'>
        {titleText === 'Search Results'
          ? 'No questions match your search criteria.'
          : 'No questions have been asked yet.'}
      </p>
    </div>
  );

  const renderQuestionList = () => (
    <div className='space-y-4'>
      {qlist.map((q, index) => (
        <React.Fragment key={String(q._id)}>
          <QuestionView question={q} />
          {index < qlist.length - 1 && <Separator />}
        </React.Fragment>
      ))}
    </div>
  );

  // Determine content to render based on state
  let content;
  if (loading) {
    content = renderLoadingState();
  } else if (qlist.length > 0) {
    content = renderQuestionList();
  } else {
    content = renderNoQuestionsState();
  }

  // Define breadcrumb items
  const breadcrumbItems: BreadcrumbItemProps[] = [
    { label: 'Home', href: '/home' },
    { label: titleText === 'Search Results' ? 'Search Results' : 'All Questions' },
  ];

  return (
    <div className='container py-8'>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav items={breadcrumbItems} />
      <div className='flex flex-row gap-4 justify-between'>
        <Input
          id='searchBar'
          placeholder='Search ...'
          type='text'
          value={val}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className='max-w-[75%]'
        />
        <div className='flex justify-end mb-4'>
          <AskQuestionButton />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className='text-2xl'>{titleText}</CardTitle>
          <QuestionHeader
            titleText={titleText}
            qcnt={qlist.length}
            currentOrder={questionOrder}
            setQuestionOrder={setQuestionOrder}
          />
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant='destructive' className='mb-4'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {content}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuestionPage;
